import { useEffect, useRef, useCallback, createContext, useContext, useState } from 'react';
import { message } from 'antd';

const WSContext = createContext(null);

export function WSProvider({ children }) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef(new Map());
  const reconnectTimerRef = useRef(null);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    // ⚠️ ХРУПКО: без слэша на конце. На /ws/ бэкенд отвечает 403 —
    // из-за этого живые обновления доски были сломаны в бою.
    const wsUrl = `${protocol}//${host}/ws?token=${token}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    ws.onclose = () => {
      setConnected(false);
      reconnectTimerRef.current = setTimeout(connect, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const type = data.type;
        const handlers = listenersRef.current.get(type);
        if (handlers) {
          handlers.forEach(fn => fn(data));
        }
        const allHandlers = listenersRef.current.get('*');
        if (allHandlers) {
          allHandlers.forEach(fn => fn(data));
        }
      } catch (e) { /* ignore non-json */ }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [connect]);

  const subscribe = useCallback((type, handler) => {
    if (!listenersRef.current.has(type)) {
      listenersRef.current.set(type, new Set());
    }
    listenersRef.current.get(type).add(handler);
    return () => {
      listenersRef.current.get(type)?.delete(handler);
    };
  }, []);

  return (
    <WSContext.Provider value={{ connected, subscribe }}>
      {children}
    </WSContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WSContext);
}

export function useWSNotification(handler) {
  const { subscribe } = useWebSocket();
  useEffect(() => {
    return subscribe('notification', handler);
  }, [subscribe, handler]);
}
